// src/theme/theme.ts
//
// Theme factory: buildTheme("dark" | "light").
// - dark  = the original sophisticated dark theme (indigo accents).
// - light = the LocateNow BRAND blue/white — same tokens as the client-link
//   /s/ deal room and the marketing site (#075FFF on #EEF1F6/white).
// All component overrides are parameterized by the mode palette, so both
// modes stay consistent without per-component retrofits. The static
// `gradients` / `shadows` exports remain (dark-accent set) for the handful
// of legacy importers; new code should read from the active theme instead.
import { createTheme, alpha, Theme } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark';

// Minimum interactive size (px) per accessibility — use for tap targets in
// new components; existing components are retrofitted lane-by-lane.
export const TOUCH_TARGET = 44;

// Custom color palette - sophisticated dark theme with vibrant accents
const darkPalette = {
  primary: {
    main: '#6366F1',
    light: '#818CF8',
    dark: '#4F46E5',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#F97316',
    light: '#FB923C',
    dark: '#EA580C',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    contrastText: '#000000',
  },
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0EA5E9',
    light: '#38BDF8',
    dark: '#0284C7',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#0F172A',
    paper: '#1E293B',
  },
  text: {
    primary: '#F1F5F9',
    secondary: '#94A3B8',
    disabled: '#64748B',
  },
  divider: 'rgba(148, 163, 184, 0.12)',
};

// LocateNow brand blue/white — mirrors the client-link /s/ page tokens
// (#075fff primary, #eef1f6 page, white cards, #111827/#475569 text).
const lightPalette = {
  primary: {
    main: '#075FFF',
    light: '#5B8BFF',
    dark: '#0B3FAE',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#F97316',
    light: '#FB923C',
    dark: '#EA580C',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#059669',
    light: '#34D399',
    dark: '#047857',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#D97706',
    light: '#F59E0B',
    dark: '#B45309',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#DC2626',
    light: '#EF4444',
    dark: '#B91C1C',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0284C7',
    light: '#38BDF8',
    dark: '#075985',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#EEF1F6',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#111827',
    secondary: '#475569',
    disabled: '#94A3B8',
  },
  divider: '#E5E7EB',
};

const buildGradients = (mode: ThemeMode) =>
  mode === 'dark'
    ? {
        primary: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)',
        secondary: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
        success: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
        dark: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
        glass: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
        cardHover: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.05) 100%)',
      }
    : {
        primary: 'linear-gradient(135deg, #075FFF 0%, #2F7BFF 50%, #5B8BFF 100%)',
        secondary: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
        success: 'linear-gradient(135deg, #059669 0%, #34D399 100%)',
        dark: 'linear-gradient(135deg, #FFFFFF 0%, #EEF1F6 100%)',
        glass: 'linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.4) 100%)',
        cardHover: 'linear-gradient(135deg, rgba(7,95,255,0.06) 0%, rgba(91,139,255,0.04) 100%)',
      };

const buildShadows = (mode: ThemeMode, primaryMain: string) =>
  mode === 'dark'
    ? {
        glow: {
          primary: `0 0 20px ${alpha(primaryMain, 0.4)}`,
          success: `0 0 20px ${alpha('#10B981', 0.4)}`,
          warning: `0 0 20px ${alpha('#F59E0B', 0.4)}`,
          error: `0 0 20px ${alpha('#EF4444', 0.4)}`,
        },
        card: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
        cardHover: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        float: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      }
    : {
        glow: {
          primary: `0 0 18px ${alpha(primaryMain, 0.25)}`,
          success: `0 0 18px ${alpha('#059669', 0.25)}`,
          warning: `0 0 18px ${alpha('#D97706', 0.25)}`,
          error: `0 0 18px ${alpha('#DC2626', 0.25)}`,
        },
        card: '0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.08)',
        cardHover: '0 12px 20px -6px rgba(16, 24, 40, 0.14), 0 4px 8px -4px rgba(16, 24, 40, 0.1)',
        float: '0 25px 50px -12px rgba(16, 24, 40, 0.25)',
      };

// Back-compat static exports (dark accent set) — legacy importers only.
export const gradients = buildGradients('dark');
export const shadows = buildShadows('dark', darkPalette.primary.main);

export const buildTheme = (mode: ThemeMode): Theme => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  const modeGradients = buildGradients(mode);
  const modeShadows = buildShadows(mode, palette.primary.main);

  return createTheme({
    palette: {
      mode,
      ...palette,
    },
    typography: {
      fontFamily: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: { fontWeight: 800, fontSize: '2.5rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
      h3: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.4 },
      h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.5 },
      h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
      subtitle1: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.5, color: palette.text.secondary },
      subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.5, letterSpacing: '0.02em', textTransform: 'uppercase', color: palette.text.secondary },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.6 },
      button: { fontWeight: 600, letterSpacing: '0.01em', textTransform: 'none' },
      caption: { fontSize: '0.75rem', lineHeight: 1.5, color: palette.text.secondary },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background.default,
            backgroundImage:
              mode === 'dark'
                ? `radial-gradient(ellipse at top left, ${alpha('#6366F1', 0.08)} 0%, transparent 50%), radial-gradient(ellipse at bottom right, ${alpha('#8B5CF6', 0.06)} 0%, transparent 50%)`
                : `radial-gradient(ellipse at top left, ${alpha('#075FFF', 0.05)} 0%, transparent 50%), radial-gradient(ellipse at bottom right, ${alpha('#5B8BFF', 0.04)} 0%, transparent 50%)`,
            minHeight: '100vh',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, padding: '10px 20px', fontSize: '0.9375rem', transition: 'all 0.2s ease-in-out' },
          contained: {
            background: modeGradients.primary,
            boxShadow: 'none',
            '&:hover': { boxShadow: modeShadows.glow.primary, transform: 'translateY(-1px)' },
          },
          outlined: { borderWidth: 2, '&:hover': { borderWidth: 2, backgroundColor: alpha(palette.primary.main, 0.08) } },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: palette.background.paper,
            border: `1px solid ${palette.divider}`,
            borderRadius: 16,
            backdropFilter: 'blur(20px)',
            transition: 'all 0.3s ease-in-out',
            ...(mode === 'light' && { boxShadow: modeShadows.card }),
            '&:hover': { borderColor: alpha(palette.primary.main, 0.3) },
          },
        },
      },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, borderRadius: 8 },
          // Scope the tinted background to DEFAULT-color chips only. Applying
          // it to all filled chips neutralized semantic colors while keeping
          // their contrastText — e.g. white text on a pale primary tint in
          // light mode (~1.25:1, unreadable). color="success|warning|..."
          // chips keep MUI's solid bg + matching contrast text.
          filled: {
            '&.MuiChip-colorDefault': {
              backgroundColor: alpha(palette.primary.main, 0.15),
              '&:hover': { backgroundColor: alpha(palette.primary.main, 0.25) },
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'medium' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              backgroundColor: mode === 'dark' ? alpha(darkPalette.background.default, 0.5) : '#FFFFFF',
              '&:hover': { backgroundColor: mode === 'dark' ? alpha(darkPalette.background.default, 0.7) : '#FFFFFF' },
              '&.Mui-focused': { backgroundColor: mode === 'dark' ? darkPalette.background.default : '#FFFFFF' },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 20, border: `1px solid ${palette.divider}`, backgroundImage: 'none', boxShadow: modeShadows.float },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(palette.background.default, 0.5),
            '& .MuiTableCell-head': { fontWeight: 600, fontSize: '0.75rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: palette.text.secondary },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.15s ease-in-out',
            '&:hover': { backgroundColor: alpha(palette.primary.main, 0.04) },
            '&.Mui-selected': { backgroundColor: alpha(palette.primary.main, 0.08) },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 3, borderRadius: '3px 3px 0 0', background: modeGradients.primary },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem', '&.Mui-selected': { color: palette.primary.main } },
        },
      },
      MuiAccordion: {
        defaultProps: { elevation: 0, disableGutters: true },
        styleOverrides: {
          root: { backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 16, '&:before': { display: 'none' } },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 600 },
          colorDefault: { backgroundColor: alpha(palette.primary.main, 0.2), color: mode === 'dark' ? palette.primary.light : palette.primary.dark },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: { borderRadius: 16, boxShadow: modeShadows.card },
          primary: { background: modeGradients.primary, '&:hover': { boxShadow: modeShadows.glow.primary } },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            '& .MuiSlider-thumb': { width: 20, height: 20, '&:hover, &.Mui-focusVisible': { boxShadow: modeShadows.glow.primary } },
            '& .MuiSlider-track': { background: modeGradients.primary, border: 'none' },
            '& .MuiSlider-rail': { backgroundColor: alpha(palette.text.secondary, 0.2) },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, border: '1px solid' },
          standardSuccess: { backgroundColor: alpha(palette.success.main, 0.1), borderColor: alpha(palette.success.main, 0.3) },
          standardError: { backgroundColor: alpha(palette.error.main, 0.1), borderColor: alpha(palette.error.main, 0.3) },
          standardWarning: { backgroundColor: alpha(palette.warning.main, 0.1), borderColor: alpha(palette.warning.main, 0.3) },
          standardInfo: { backgroundColor: alpha(palette.info.main, 0.1), borderColor: alpha(palette.info.main, 0.3) },
        },
      },
    },
  });
};

// Create the theme (back-compat default: dark)
export const theme = buildTheme('dark');

export default theme;
